import { format } from 'd3-format';

import { FilterId } from '../../../next/CustomDash/filters/types/filters';

interface FormatCurrencyValueOptions {
  value: number;
  exchangeRate: number;
  currencySymbol: string;
  filterId: FilterId;
  isPayFilter?: boolean;
  isFundingFilter?: boolean;
  isRateFilter?: boolean;
}

/**
 * Formats a numeric value for display in filter chips, considering currency conversion and formatting rules.
 */
export function formatFilterCurrencyValue({
  value,
  exchangeRate,
  currencySymbol,
  isPayFilter = false,
  isFundingFilter = false,
  isRateFilter = false,
}: FormatCurrencyValueOptions): string {
  if (isPayFilter) {
    const convertedValue = Math.round(value * exchangeRate);
    return formatCurrencyAmount(convertedValue, currencySymbol);
  }

  if (isFundingFilter) {
    const convertedValue = value * exchangeRate;
    return formatCurrencyAmount(convertedValue, currencySymbol);
  }

  if (isRateFilter) {
    return `${value}%`;
  }

  return String(value);
}

/**
 * Formats a currency amount with appropriate thousands separators and abbreviations.
 */
function formatCurrencyAmount(value: number, symbol: string): string {
  if (value >= 1_000_000) {
    const formatted = format('.3s')(value).replace('G', 'B');
    const withoutTrailingZeros = formatted.replace(/\.0+([A-Za-z])/, '$1');
    return `${symbol}${withoutTrailingZeros}`;
  }

  if (value >= 1_000) {
    return `${symbol}${format(',')(value)}`;
  }

  return `${symbol}${value.toLocaleString()}`;
}

/**
 * Determines if a filter ID represents a funding-related filter.
 */
export function isFundingFilterId(filterId: FilterId): boolean {
  return (
    filterId === FilterId.LAST_FUNDING_AMOUNT ||
    filterId === FilterId.TOTAL_FUNDING_AMOUNT
  );
}

/**
 * Determines if a filter ID represents a rate-based filter.
 */
export function isRateFilterId(filterId: FilterId): boolean {
  return (
    filterId === FilterId.ATTRITION_RATE ||
    filterId === FilterId.HIRING_RATE ||
    filterId === FilterId.GROWTH_RATE
  );
}
